<script setup>
import { computed } from 'vue'
import { TabGroup, TabList, Tab } from '@headlessui/vue'

import AppButton from '@/components/atoms/AppButton'

const props = defineProps({
  modelValue: { type: String, required: true },
  options: { type: Array, required: true }, // [{ label, value }]
  disabled: { type: Boolean, default: false }, // dims + blocks the whole toggle
  // 'pill' (default) — boxed view switcher · 'link' — page tab bar, active tab underlined
  variant: { type: String, default: 'pill' },
  /**
   * true (default) — equal-width pills (fixed w-20), for short/symmetric labels
   *   like Overview / Inspector.
   * false — each tab sizes to its own label, for long/variable labels
   *   (e.g. Order status: "Ready to List", "Out of Stock"). Wrap the toggle
   *   in an overflow-x-auto container if the label set can exceed the row.
   */
  equalWidth: { type: Boolean, default: true },
  /**
   * link variant: tabs hug their labels, dropping the 10px of side padding each
   * one carries — for a narrow strip (the mobile report bar). The 24px GAP is
   * no longer part of this: it is the spacing for every link tab bar now, not a
   * compact-only affordance.
   */
  compact: { type: Boolean, default: false },
  class: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

const selectedIndex = computed(() => props.options.findIndex(({ value }) => value === props.modelValue))
</script>

<template>
  <TabGroup
    v-if="variant === 'link'"
    :selected-index="selectedIndex"
    @change="(index) => emit('update:modelValue', options[index].value)"
  >
    <TabList
      class="flex overflow-x-auto border-b border-gray-200 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      :class="['gap-x-6', $props.class, disabled ? 'opacity-60 pointer-events-none' : '']"
    >
      <Tab
        v-for="option in options"
        :key="option.value"
        :disabled="disabled"
        class="shrink-0 whitespace-nowrap border-b-2 py-3 text-sm"
        :class="[
          compact ? '' : 'px-2.5',
          modelValue === option.value ? 'border-tint-1 font-semibold text-tint-1' : 'border-transparent text-gray-500',
        ]"
        v-text="option.label"
      />
    </TabList>
  </TabGroup>

  <div
    v-else
    class="bg-gray-50 ring-1 ring-inset ring-gray-200 rounded-[6px] h-7 flex items-stretch gap-1 overflow-hidden"
    :class="[$props.class, disabled ? 'opacity-60 pointer-events-none' : '']"
  >
    <AppButton
      v-for="option in options"
      :key="option.value"
      :variant="modelValue === option.value ? 'tab-active' : 'tab'"
      size="sm"
      :disabled="disabled"
      class="!h-full"
      :class="equalWidth ? '' : '!w-auto !px-3'"
      @click="emit('update:modelValue', option.value)"
    >
      {{ option.label }}
    </AppButton>
  </div>
</template>
